/** Sprint 5-d PR-4 + Sub-PR E2 · AI 對話全頁（sidebar + 頂部列 + 功能磚 + timeline + 作業中狀態 + 功能 modal）。 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Drawer, IconButton,
  TextField, Tooltip, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import AppsIcon from '@mui/icons-material/Apps';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import ModeSwitcher from '../../../components/ModeSwitcher';

import { useMessage } from '../../../contexts/MessageContext';
import { useSystem } from '../../../contexts/SystemContext';
import { getI18nValue } from '../../../utils/i18nHelper';
import { flattenFunctions } from '../../../utils/functionsTree';
import { systemService } from '../../../api/systemService';
import type { SystemFunction } from '../../../types';
import SessionSidebar from './components/SessionSidebar';
import AIChatProfileMenu from './components/AIChatProfileMenu';
import MessageList from './components/MessageList';
import MessageInput from './components/MessageInput';
import ProviderIndicator from './components/ProviderIndicator';
import FunctionTilesPopover, { TileAction } from './components/FunctionTilesPopover';
import FunctionModal, { FunctionModalTarget, isEmbeddable } from './components/FunctionModal';
import ScopeProjectContent from './components/ScopeProjectContent';
import { WorkPhase } from './components/ChatWorkingStatus';
import { BillAttachmentItem, TimelineItem } from './types';
import { downloadMyBillAttachment } from '../../../services/platformBillingService';
import {
  ChatSession, ToolCategory, cancelAction, confirmAction, createSession, deleteSession,
  getAvailableTools, getCost, getScopes, getSession, listSessions, patchSession, streamMessage,
  type VoiceMeta,
} from './services/aiChatService';

let _seq = 0;
const nextKey = () => `it-${Date.now()}-${_seq++}`;

const ChatPage: React.FC = () => {
  const { sessionId, funcCode: scopeParam } = useParams();
  const currentId = sessionId ? Number(sessionId) : null;
  const scopeProjectMode = Boolean(scopeParam);   // §5.5/§2：功能專案頁嵌在聊天版面主區
  const nav = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { showSuccess, showApiError } = useMessage();
  const { getSystemTitle } = useSystem();

  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [items, setItems] = useState<TimelineItem[]>([]);
  const [input, setInput] = useState('');
  // 語音輸入訓練資料（§2.6）：目前輸入是否來自語音·辨識原文·語言。送出後 reset。
  const [voiceMeta, setVoiceMeta] = useState<VoiceMeta>({ input_channel: 'text' });
  const [streaming, setStreaming] = useState(false);
  const [sessionCost, setSessionCost] = useState(0);
  const [categories, setCategories] = useState<ToolCategory[]>([]);
  const [fallback, setFallback] = useState<{ actual_provider?: string; actual_model?: string; reason?: string } | null>(null);
  const [renameFor, setRenameFor] = useState<ChatSession | null>(null);
  const [renameVal, setRenameVal] = useState('');
  const [deleteFor, setDeleteFor] = useState<ChatSession | null>(null);
  // Sub-PR E2：功能磚 / 功能 modal / 作業中狀態 / 當前範圍
  const [functions, setFunctions] = useState<SystemFunction[]>([]);
  const [scopes, setScopes] = useState<Set<string>>(new Set());
  const [tilesAnchor, setTilesAnchor] = useState<HTMLElement | null>(null);
  const [modal, setModal] = useState<FunctionModalTarget | null>(null);
  const [workPhase, setWorkPhase] = useState<WorkPhase | null>(null);
  const [workTool, setWorkTool] = useState<string | undefined>(undefined);
  const [currentScope, setCurrentScope] = useState<string | null>(null);
  const [confirmBusy, setConfirmBusy] = useState<number | null>(null);   // E5 進行中的 actionId
  // RWD：桌機收折（localStorage 記憶）/ 手機抽屜
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem('ai_chat_sidebar_collapsed') === '1'; } catch { return false; }
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const toggleCollapse = () => setCollapsed((v) => {
    const nv = !v;
    try { localStorage.setItem('ai_chat_sidebar_collapsed', nv ? '1' : '0'); } catch { /* silent ok: 無痕/封鎖 */ }
    return nv;
  });

  const streamingRef = useRef(false);
  const pendingSentRef = useRef<string | null>(null);
  const sidebarScopeRef = useRef<string | null>(null);

  // §5.5 左側列表依當前情境過濾：專案頁→該功能；對話→該對話所屬範圍；一般對話（不限範圍）首頁→global
  const sidebarScope = scopeProjectMode ? (scopeParam || null) : currentScope;
  const reloadSessions = useCallback(async () => {
    try { setSessions(await listSessions(false, sidebarScopeRef.current || 'global')); }
    catch (err) { showApiError(err); }
  }, [showApiError]);

  useEffect(() => { sidebarScopeRef.current = sidebarScope; reloadSessions(); }, [sidebarScope, reloadSessions]);
  useEffect(() => { getAvailableTools().then((r) => setCategories(r.categories)).catch(() => { /* silent ok: 工具列載入失敗不阻擋對話 */ }); }, []);
  // 功能磚資料（GET /api/system/functions·依 role_rights 過濾·樹狀 → 攤平）+ 可對話功能清單
  useEffect(() => {
    systemService.getFunctions().then((tree) => setFunctions(flattenFunctions(tree))).catch(() => { /* silent ok: 功能磚載入失敗 */ });
    getScopes().then((rows) => setScopes(new Set(rows.map((r) => r.func_code)))).catch(() => { /* silent ok: scopes 載入失敗 */ });
  }, []);

  // 工具 name → display_name（作業中狀態顯示中文工具名·§5.7）
  const toolLabel = useCallback((name?: string): string | undefined => {
    if (!name) return undefined;
    for (const c of categories) {
      const tl = c.tools.find((x) => x.name === name);
      if (tl) return tl.display_name?.[i18n.language] || tl.label || tl.name;
    }
    return name;
  }, [categories, i18n.language]);

  const toolRequiresConfirm = useCallback((name?: string): boolean => {
    for (const c of categories) {
      const tl = c.tools.find((x) => x.name === name);
      if (tl) return !!tl.requires_confirmation;
    }
    return false;
  }, [categories]);

  const funcByCode = useCallback((code: string) =>
    functions.find((f) => f.func_code === code), [functions]);

  // 載入當前 session 的 history（串流中不重載）
  useEffect(() => {
    if (streamingRef.current) return;
    if (!currentId) { setItems([]); setSessionCost(0); setFallback(null); setCurrentScope(null); return; }
    (async () => {
      try {
        const { session, messages, pending_actions } = await getSession(currentId);
        setSessionCost(session.total_cost_ntd || 0);
        setCurrentScope(session.session_scope);
        // 首句 pendingMessage 串流可能已在 await 期間開始；再查一次避免覆蓋即時內容（回覆框消失）
        if (streamingRef.current) return;
        setFallback(null);
        const base: TimelineItem[] = messages.map((m) => ({
          key: `m-${m.id}`, kind: m.role === 'user' ? 'user' : 'assistant', content: m.content,
        }));
        // E5 §5.1：還原仍 pending 的確認卡（executed/cancelled 已由固定文字訊息呈現）
        (pending_actions || []).filter((p) => p.status === 'pending').forEach((p) => base.push({
          key: `pa-${p.id}`, kind: 'confirm_card', content: '', actionId: p.id,
          confirmSummary: p.summary, confirmStatus: 'pending', confirmExpiresAt: p.expires_at,
        }));
        setItems(base);
      } catch (err) { showApiError(err); }
    })();
  }, [currentId, showApiError]);

  const openModalForNavigate = useCallback((funcCode: string, funcName: FunctionModalTarget['funcName'], prefill?: Record<string, string>) => {
    if (isEmbeddable(funcCode)) setModal({ funcCode, funcName, prefill });
    else nav(`/${funcCode}`);   // 未支援內嵌 → 跳頁
  }, [nav]);

  const doSend = async (explicit?: string) => {
    const content = (explicit ?? input).trim();
    if (!content || streaming) return;
    // 語音 meta 僅對應手動輸入框內容（explicit=新對話首句重送·無 meta）
    const meta: VoiceMeta = explicit === undefined ? voiceMeta : { input_channel: 'text' };
    if (explicit === undefined) { setInput(''); setVoiceMeta({ input_channel: 'text' }); }
    const sid = currentId;
    if (!sid) {
      // 新對話第一句：建立對話後換網址並帶 pendingMessage，由新頁面掛載後的 effect 送出。
      // 不可在此實例續串流——nav 會讓 AIChatLayout 的 ErrorBoundary key={pathname} 重建 ChatPage，
      // 串流會留在已卸載的舊實例，確認卡 / modal / 作業中狀態全失效（沿用 startScope 的做法）。
      try {
        const s = await createSession();
        showSuccess('SYS230001', { name: content.slice(0, 20) });
        await reloadSessions();
        nav(`/ee_ai_chat/${s.id}`, { replace: true, state: { pendingMessage: content } });
      } catch (err) { showApiError(err); }
      return;
    }
    const userItem: TimelineItem = { key: nextKey(), kind: 'user', content };
    const asstItem: TimelineItem = { key: nextKey(), kind: 'assistant', content: '', pending: true };
    setItems((prev) => [...prev, userItem, asstItem]);
    setFallback(null);
    setStreaming(true); streamingRef.current = true;
    setWorkPhase('thinking'); setWorkTool(undefined);

    const appendAssistant = (text: string) => setItems((prev) => prev.map(
      (it) => (it.key === asstItem.key ? { ...it, content: it.content + text } : it)));
    const insertBeforeAssistant = (node: TimelineItem) => setItems((prev) => {
      const i = prev.findIndex((x) => x.key === asstItem.key);
      if (i < 0) return [...prev, node];
      return [...prev.slice(0, i), node, ...prev.slice(i)];
    });

    await streamMessage(sid, content, {
      onStart: (d) => {
        // §6-1 應修#2：使用者改口 → 後端作廢的舊確認卡標為已過期
        const ids = d.expired_action_ids || [];
        if (ids.length) setItems((prev) => prev.map(
          (it) => (it.actionId && ids.includes(it.actionId) ? { ...it, confirmStatus: 'expired' } : it)));
      },
      onDelta: (text) => { setWorkPhase('replying'); appendAssistant(text); },
      onToolUse: (d) => {
        setWorkPhase('querying'); setWorkTool(d.name);   // 存代碼·顯示名稱於渲染時才查
        // 只存工具代碼；display_name 與「等待您確認」由 MessageList 渲染時依最新 categories 決定
        // （首句時工具清單可能尚未載完·避免顯示 list_my_events 原始代碼）
        insertBeforeAssistant({
          key: nextKey(), kind: 'tool_use', toolCode: d.name, content: '' });
      },
      onToolResult: (d) => {
        setWorkPhase('replying');
        const c = (d.content ?? {}) as { action?: string; func_code?: string; func_name?: never; path?: string; prefill?: Record<string, string> };
        if (c && c.action === 'navigate' && c.func_code) {
          // §5.8：不顯示原始 JSON·改留「已開啟」卡片並自動開 modal
          insertBeforeAssistant({
            key: nextKey(), kind: 'navigate_card', content: '',
            navFuncCode: c.func_code, navFuncName: c.func_name, navPrefill: c.prefill });
          openModalForNavigate(c.func_code, c.func_name, c.prefill);
        } else if (c && c.action === 'contact') {
          // §12 員工聯絡卡片：結構化卡片（含可撥打 tel: 連結）·不顯示原始 JSON
          insertBeforeAssistant({
            key: nextKey(), kind: 'contact_card', content: '',
            contact: d.content as TimelineItem['contact'] });
        } else if (c && c.action === 'bill_attachments') {
          // my_bills §3.3 帳單附件下載卡片：走既有下載端點帶登入憑證·檔案不進對話
          insertBeforeAssistant({
            key: nextKey(), kind: 'bill_attachments_card', content: '',
            billAttachments: d.content as TimelineItem['billAttachments'] });
        } else {
          insertBeforeAssistant({
            key: nextKey(), kind: 'tool_result', content: JSON.stringify(d.content ?? {}, null, 2) });
        }
      },
      onConfirmRequired: (d) => {
        setWorkPhase(null);   // §5.2 確認卡出現後作業中列停止
        insertBeforeAssistant({
          key: nextKey(), kind: 'confirm_card', content: '', actionId: d.pending_action_id,
          confirmSummary: d.summary, confirmStatus: 'pending', confirmExpiresAt: d.expires_at });
      },
      onActionResult: (d) => {
        // 文字「確定/取消」路徑：更新對應確認卡狀態 + 顯示固定結果訊息
        if (d.action_id) {
          setItems((prev) => prev.map((it) => (it.actionId === d.action_id
            ? { ...it, confirmStatus: (d.status as TimelineItem['confirmStatus']) } : it)));
        }
        if (d.message) setItems((prev) => [...prev, { key: nextKey(), kind: 'assistant', content: d.message! }]);
      },
      onFallback: (d) => setFallback(d),
      onStop: async () => {
        setItems((prev) => prev.map((it) => (it.key === asstItem.key ? { ...it, pending: false } : it)));
        setWorkPhase(null); setWorkTool(undefined);
        try { const c = await getCost(sid!); setSessionCost(c.total_cost_ntd || 0); } catch { /* silent ok: 成本刷新失敗不阻擋 */ }
        reloadSessions();
      },
      onError: (d) => {
        setItems((prev) => prev.filter((it) => it.key !== asstItem.key));
        setWorkPhase(null); setWorkTool(undefined);
        showApiError({ response: { data: { detail: d.code || 'ERR230003' } } });
      },
    }, undefined, meta);
    setStreaming(false); streamingRef.current = false;
    setWorkPhase(null);
  };

  // §5.3：從功能專案頁帶第一句進來（location.state.pendingMessage）→ 自動送出一次
  useEffect(() => {
    const pending = (location.state as { pendingMessage?: string } | null)?.pendingMessage;
    if (currentId && pending && pendingSentRef.current !== `${currentId}:${pending}`) {
      pendingSentRef.current = `${currentId}:${pending}`;
      nav(location.pathname, { replace: true, state: {} });   // 清 state 防重送
      doSend(pending);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId, location.state]);

  const onNew = async () => {
    // 專案（範圍）情境下「新對話」留在同一專案：回專案頁，輸入第一句時才建對話（D3·不產空對話）
    const scope = scopeProjectMode ? (scopeParam || null) : currentScope;
    if (scope) { nav(`/ee_ai_chat/scope/${scope}`); return; }
    try {
      const s = await createSession();
      await reloadSessions();
      showSuccess('SYS230001', { name: t('chat.newSession', '新對話') });
      nav(`/ee_ai_chat/${s.id}`);
    } catch (err) { showApiError(err); }
  };

  const doPin = async (s: ChatSession) => {
    try { await patchSession(s.id, { is_pinned: !s.is_pinned }); showSuccess('SYS230003', { name: s.title || '' }); reloadSessions(); }
    catch (err) { showApiError(err); }
  };
  const doArchive = async (s: ChatSession) => {
    try { await patchSession(s.id, { is_archived: !s.is_archived }); showSuccess('SYS230004', { name: s.title || '' }); reloadSessions(); }
    catch (err) { showApiError(err); }
  };
  const doRenameSave = async () => {
    if (!renameFor) return;
    try { await patchSession(renameFor.id, { title: renameVal.trim() }); showSuccess('SYS230002', { name: renameVal.trim() }); setRenameFor(null); reloadSessions(); }
    catch (err) { showApiError(err); }
  };
  const doDeleteConfirm = async () => {
    if (!deleteFor) return;
    const id = deleteFor.id;
    try {
      await deleteSession(id);
      showSuccess('SYS020001', { name: deleteFor.title || '' });
      setDeleteFor(null);
      await reloadSessions();
      if (id === currentId) nav('/ee_ai_chat');
    } catch (err) { showApiError(err); }
  };

  // E5 §5.1 確認卡：確認 / 取消（呼叫後端·不經 AI）
  const setCardStatus = (actionId: number, st: TimelineItem['confirmStatus']) =>
    setItems((prev) => prev.map((x) => (x.actionId === actionId ? { ...x, confirmStatus: st } : x)));
  const doConfirmCard = async (it: TimelineItem) => {
    if (!currentId || !it.actionId || confirmBusy) return;
    setConfirmBusy(it.actionId);
    try {
      const r = await confirmAction(currentId, it.actionId);
      setCardStatus(it.actionId, (r.status as TimelineItem['confirmStatus']) || 'executed');
      if (r.message) setItems((prev) => [...prev, { key: nextKey(), kind: 'assistant', content: r.message! }]);
      // lint-msg-allow: 後端回傳的 9 碼確認結果代碼（SYS230008 完成 / SYS230009 取消）
      if (r.code) showSuccess(r.code);
      reloadSessions();
    } catch (err) {
      setCardStatus(it.actionId, 'expired');   // 409 過期/已處理
      showApiError(err);
    } finally { setConfirmBusy(null); }
  };
  const doCancelCard = async (it: TimelineItem) => {
    if (!currentId || !it.actionId || confirmBusy) return;
    setConfirmBusy(it.actionId);
    try {
      const r = await cancelAction(currentId, it.actionId);
      setCardStatus(it.actionId, 'cancelled');
      if (r.message) setItems((prev) => [...prev, { key: nextKey(), kind: 'assistant', content: r.message! }]);
    } catch (err) { showApiError(err); } finally { setConfirmBusy(null); }
  };

  const onTileAction = (a: TileAction) => {
    if (a.kind === 'scope') nav(`/ee_ai_chat/scope/${a.funcCode}`);
    else if (a.kind === 'modal') setModal({ funcCode: a.funcCode, funcName: a.funcName });
    else nav(`/${a.funcCode}`);
  };

  // my_bills §3.3：帳單附件下載（走既有租戶端點帶登入憑證·內容不進對話）
  const downloadBillAttachment = async (billId: number, att: BillAttachmentItem) => {
    try {
      const blob = await downloadMyBillAttachment(billId, att.att_id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = att.filename || 'attachment';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { showApiError(err); }
  };

  // §5.3 專案頁送第一句 / 點選項 → 建範圍對話並帶第一句進對話（ChatPage 依 state.pendingMessage 自動送）
  const startScope = async (text: string) => {
    const content = text.trim();
    if (!content || streaming || !scopeParam) return;
    try {
      const s = await createSession({ session_scope: scopeParam });
      nav(`/ee_ai_chat/${s.id}`, { state: { pendingMessage: content } });
    } catch (err) { showApiError(err); }
  };

  const brand = getSystemTitle() || 'AI';
  const activeScope = scopeProjectMode ? (scopeParam || null) : currentScope;   // 頂部列範圍所依
  const scopeFunc = activeScope ? funcByCode(activeScope) : null;
  const scopeName = activeScope
    ? `${scopeFunc?.func_icon ? scopeFunc.func_icon + ' ' : ''}${scopeFunc ? getI18nValue(scopeFunc.func_name, i18n.language) : activeScope}`
    : '';
  const modalTitle = modal
    ? (getI18nValue(modal.funcName as never, i18n.language) || getI18nValue(funcByCode(modal.funcCode)?.func_name, i18n.language) || modal.funcCode)
    : '';

  const profileFooter = (
    <AIChatProfileMenu functions={functions} scopes={scopes} sessionCostNtd={sessionCost}
      onOpenFunction={(fc, fn) => setModal({ funcCode: fc, funcName: fn })} />
  );
  const sidebar = (inDrawer: boolean) => (
    <SessionSidebar
      sessions={sessions} currentId={currentId} functions={functions}
      collapsed={!inDrawer && collapsed}
      onToggleCollapse={inDrawer ? undefined : toggleCollapse}
      onNew={() => { if (inDrawer) setDrawerOpen(false); onNew(); }}
      onSelect={(id) => { if (inDrawer) setDrawerOpen(false); nav(`/ee_ai_chat/${id}`); }}
      onRename={(s) => { setRenameFor(s); setRenameVal(s.title || ''); }}
      onPin={doPin} onArchive={doArchive} onDelete={(s) => setDeleteFor(s)}
      footer={profileFooter}
    />
  );

  return (
    <Box sx={{ display: 'flex', height: '100%' }}>
      {/* 桌機（≥md）常駐可收折側欄；手機（<md）抽屜 */}
      {!isMobile && sidebar(false)}
      {isMobile && (
        <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}
          PaperProps={{ sx: { width: 'min(80vw, 300px)' } }}>
          {sidebar(true)}
        </Drawer>
      )}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* §4.3 頂部列：模式下拉（最左）+（有範圍時）› 功能名 ✕ + 功能磚按鈕 */}
        <Box sx={{
          height: 48, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2, borderBottom: '1px solid', borderColor: 'divider',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            {isMobile && (
              <Tooltip title={t('chat.sidebar.open', '對話列表')}>
                <IconButton size="small" onClick={() => setDrawerOpen(true)}><MenuIcon /></IconButton>
              </Tooltip>
            )}
            <ModeSwitcher variant="surface" />
            {activeScope && (
              <>
                <Typography variant="body2" sx={{ color: 'text.disabled' }}>›</Typography>
                <Typography variant="subtitle2" noWrap
                  onClick={() => !scopeProjectMode && nav(`/ee_ai_chat/scope/${activeScope}`)}
                  sx={{ fontWeight: 600, cursor: scopeProjectMode ? 'default' : 'pointer' }}>
                  {scopeName}
                </Typography>
                <Tooltip title={t('chat.topbar.exitScope', '離開此功能')}>
                  <IconButton size="small" onClick={() => nav('/ee_ai_chat')}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
          <Tooltip title={t('chat.topbar.tiles', '功能磚')}>
            <IconButton onClick={(e) => setTilesAnchor(e.currentTarget)} size="small">
              <AppsIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <ProviderIndicator notice={fallback} onClose={() => setFallback(null)} />
        {scopeProjectMode && scopeParam ? (
          // §5.3/§5.5 功能專案頁（嵌在版面主區·左側列表已過濾為本專案）
          <ScopeProjectContent
            funcCode={scopeParam} funcObj={funcByCode(scopeParam) || null}
            busy={streaming}
            onStart={startScope}
            onEdit={() => setModal({ funcCode: scopeParam, funcName: funcByCode(scopeParam)?.func_name })}
            onSelectSession={(id) => nav(`/ee_ai_chat/${id}`)}
          />
        ) : items.length === 0 ? (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, px: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              ✦ {t('chat.welcome', '歡迎使用 {{name}} AI 助手', { name: brand })}
            </Typography>
            <Box sx={{ width: '100%', maxWidth: 720 }}>
              <MessageInput value={input} onChange={setInput} onSend={() => doSend()} busy={streaming} onVoiceMeta={setVoiceMeta} />
            </Box>
          </Box>
        ) : (
          <>
            <MessageList
              items={items} streaming={streaming} workPhase={workPhase} workTool={workTool}
              toolLabel={toolLabel} toolRequiresConfirm={toolRequiresConfirm}
              onReopenCard={(it) => it.navFuncCode && openModalForNavigate(it.navFuncCode, it.navFuncName, it.navPrefill)}
              onConfirm={doConfirmCard} onCancel={doCancelCard} confirmBusy={confirmBusy}
              onDownloadBillAttachment={downloadBillAttachment}
            />
            <Box sx={{ width: '100%', maxWidth: 860, mx: 'auto' }}>
              <MessageInput value={input} onChange={setInput} onSend={() => doSend()} busy={streaming} onVoiceMeta={setVoiceMeta} />
            </Box>
          </>
        )}
      </Box>

      <FunctionTilesPopover
        open={Boolean(tilesAnchor)} anchorEl={tilesAnchor}
        functions={functions} scopes={scopes} embeddable={isEmbeddable}
        onClose={() => setTilesAnchor(null)} onAction={onTileAction}
      />
      <FunctionModal target={modal} title={modalTitle} onClose={() => setModal(null)} />

      {/* rename dialog */}
      <Dialog open={Boolean(renameFor)} onClose={() => setRenameFor(null)} fullWidth maxWidth="xs">
        <DialogTitle>{t('chat.rename', '重新命名')}</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth size="small" sx={{ mt: 1 }} value={renameVal}
            onChange={(e) => setRenameVal(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') doRenameSave(); }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameFor(null)}>{t('common.cancel', '取消')}</Button>
          <Button variant="contained" onClick={doRenameSave}>{t('common.save', '儲存')}</Button>
        </DialogActions>
      </Dialog>

      {/* delete confirm */}
      <Dialog open={Boolean(deleteFor)} onClose={() => setDeleteFor(null)} maxWidth="xs">
        <DialogTitle>{t('chat.confirmDelete', '刪除對話')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('chat.confirmDeleteHint', '確定要刪除「{{name}}」？此動作無法復原。', { name: deleteFor?.title || t('chat.untitled', '（未命名對話）') })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteFor(null)}>{t('common.cancel', '取消')}</Button>
          <Button color="error" variant="contained" onClick={doDeleteConfirm}>{t('chat.delete', '刪除')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChatPage;
